import { useMutation, useQuery } from '@apollo/client';
import { EmailEditor } from '@nile/banyan/src/Components/Editors';
import { AttachmentItemHolder } from '@nile/banyan/src/Components/Editors/RTEditor';
import {
  ALL_MODULES,
  AppContext,
  getDownloadURL,
  getHeaderValue,
  getObjectValueCount,
  getTranslatedText,
  Loader,
  T1,
  toast,
  DateTimeFormatter,
  useUserMSPCheckHook,
  useIsAuthorisedHook,
  TabContent,
  TabsContainer,
  SideConversations,
  NewSideConversation,
  isAuthorised,
  sideConversationPricingPlans,
  EmptyTabState,
  useCheckFeatureGating,
  fetchApi,
  getConversationOrder,
  scrollToBottom,
  scrollToTop,
  COLOR_MAP
} from '@nile/components';
import MultipleNamesHandler from '@nile/components/src/Pebbles/multipleNamesHandler';
import {
  AccountClient,
  CREATE_CLIENT_CONVERSATION,
  GET_CLIENT_USERS,
  UPDATE_READ_STATUS,
  GET_CLIENT_CONVERSATIONS,
  useCachedQuery,
  GET_CLIENT_CONVERSATION_REPLY_RECIPEINTS
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import Recepients from '@nile/mangifera/src/Components/Chat/Recepients';
import { Receiver, Sender } from '@nile/mangifera/src/Pebbles/chat';
import { SelectUsers } from '@nile/mangifera/src/Pebbles/ChatContainer';
import {
  Button,
  Avatar,
  Link,
  TextStyle,
  Card,
  ButtonGroup,
  DisplayText
} from '@superops/ui-design-system';
import dayjs from 'dayjs';
import Collapse from 'react-collapse';
import { EditorState } from 'draft-js';
import { cloneDeep, pick } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef
} from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { Tooltip } from 'react-clone-tippy';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import EmailFailureStatus from '@nile/mangifera/src/Components/Chat/EmailFailureState';
import { dompurifier } from '@nile/components/src/Utils/helpers';

// Shared Styled Components
export const ThreadWrapper = styled.div`
  height: ${(props) => (props.isOpen ? '100%' : 'auto')};
  .ReactCollapse--collapse {
    height: ${(props) => (props.isOpen ? '100% !important' : '')};
  }
  .ReactCollapse--content {
    height: 100% !important;
    height: ${(props) => (props.isOpen ? '100% !important' : '')};
  }
  box-shadow: 0px 10px 10px rgba(0, 0, 0, 0.05);
`;

export const Container = styled.div`
  ${(props) =>
    props.border &&
    `
    border: 3px solid white;
    border-top: 1px solid white;
    border-radius: 10px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    `}
`;

export const Wrapper = styled.div`
  height: calc(100% - 70px);
  margin-top: 1px;
  .message-content {
    word-break: break-word;
    img {
      max-width: 100% !important;
    }
  }
`;

export const ReplyThreadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-size: 360px;
  // background-image: url(${envConfig.imageRoot}svg/chatBg.svg);
  .rdw-editor-wrapper {
    margin-top: 12px !important;
    margin-bottom: 12px !important;
    border: 1px solid #ececec;
  }
  .action-buttons {
    position: absolute;
    bottom: 12px;
    right: 30px;
  }
  .action-buttons .Superops-Button--plain {
    margin-left: auto;
  }
`;

export const EditorHolder = styled.div`
  #quill-rt-editor {
    height: 100%;
    .quill {
      min-height: 100px;
      .ql-container {
        border: none;
        min-height: inherit;
        .ql-editor {
          min-height: 300px;
        }
      }
    }
    .ql-toolbar {
      border: none;
    }
  }
  .ql-editor {
    max-height: 300px;
  }
`;

export const ItemHolders = styled.div`
  div {
    &:first-child {
      margin-left: 0;
    }
  }
`;

export const AttachmentsContainer = styled(AttachmentItemHolder)`
  margin-right: 10px;
  padding: 0 1rem;
  &:hover {
    cursor: default;
  }
`;

export const CopyMailUsers = styled.div`
  margin-bottom: 0px;
  .container {
    width: 100%;
  }
  .select-wrapper {
    width: 100%;
  }
  .value_selector__control {
    border: none;
  }
`;

// Shared Helper Functions
export const getUserName = (message, intl) => {
  if (message?.user?.name || message?.user?.email) {
    return `${message.user.name || message.user.email || ''}`;
  }
  return getTranslatedText(intl, 'ticket.details.note.system');
};

export const findLastIndex = (array = []) => {
  for (let i = array.length - 1; i >= 0; i--) {
    if (array[i]?.user?.accountType === 'MSP') return i;
  }
  return -1;
};

export const getCCUsersToCarryForward = (convos) => {
  const replyIndex = findLastIndex(convos);
  let allCCUsers = [];
  if (replyIndex > -1) {
    const users = convos[replyIndex]?.ccUsers || [];
    allCCUsers = users.map((el) => {
      return el.user;
    });
    return allCCUsers;
  }
  return [];
};

export const getCCUsers = (users, toMap) => {
  return users.filter((el) => !toMap[el.email]);
};

export const getBccUsers = (users, toMap, ccMap) => {
  return users.filter((el) => !toMap[el.email] && !ccMap[el.email]);
};

// Shared Thread Header Component
export function ConversationThreadHeader({
  conversation,
  isOpen,
  toggleThread,
  getUserName
}) {
  const intl = useIntl();
  return (
    <div
      className=" white-to-grey-bg p25 cursor-pointer"
      onClick={toggleThread}
      isOpen={isOpen}
    >
      <div className="subject ellipsis w70" title={conversation.subject}>
        <DisplayText element="h6" size="extraLarge" weight="Bold">
          {conversation.subject}
        </DisplayText>
      </div>
      <div className="flex center mlauto g10">
        <TextStyle variation="subdued">
          <DateTimeFormatter type="dateTime" date={conversation.time} />
        </TextStyle>
        |
        <div
          style={{ backgroundColor: '#f2f5ff' }}
          className="flex center br50 p3 g5"
        >
          <GenericAvatar conversation={conversation} intl={intl} />
          <TextStyle variation="subdued">
            {getUserName(conversation, intl)}
          </TextStyle>
        </div>
      </div>
    </div>
  );
}

export function GenericAvatar({ conversation, intl, size = 'extraSmall' }) {
  const getUserInitials = (user) => {
    if (user?.firstName) {
      return `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`;
    }
    if (user?.name) {
      return user.name?.[0] || '';
    }
    if (user?.email) {
      return user.email?.[0] || '';
    }
    return getTranslatedText(intl, 'ticket.details.note.system')?.[0] || 'S';
  };

  const getAvatarSource = (user) => {
    if (!user?.name && !user?.email) {
      return `${envConfig.imageRoot}svg/notification.svg`;
    }
    return '';
  };

  return (
    <Avatar
      size={size}
      initials={getUserInitials(conversation?.user)}
      name={getUserName(conversation, intl)}
      source={getAvatarSource(conversation?.user)}
    />
  );
}

// Shared Conversation Bubble Component
export function ConversationBubble({
  conversation,
  userType,
  loggedInUser,
  getUserName,
  company,
  intl,
  clientId,
  isConversationViewed,
  createdTime,
  children
}) {
  const Tags = {
    sender: Sender,
    receiver: Receiver
  };
  const Tag = Tags[userType];

  return (
    <div className="mb12">
      <Tag className="rel bubbleContainer">
        <Card
          variant={userType === 'sender' ? 'discussion' : 'default'}
          padding="sizeExtraSmall"
        >
          <div className="flex">
            <div className="mr8">
              <GenericAvatar conversation={conversation} intl={intl} />
            </div>

            <div className="fg1">
              <div className="flex center">
                <TextStyle variation="strong">
                  {getUserName(conversation, intl)}
                </TextStyle>

                <div className="ml8">
                  <Tooltip
                    html={<Recepients message={conversation} />}
                    delay={300}
                    duration={250}
                    position="bottom-start"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                    theme="super-light"
                  >
                    <div className="flex center">
                      <SVGIcon name="DropSheet" size="sm" />
                    </div>
                  </Tooltip>
                </div>
                <div className="fx2 flex jend">
                  <TextStyle variation="subdued">
                    {dayjs(createdTime || conversation.time).format(
                      `${company?.dateFormat ||
                        'MMM DD YYYY'}, ${company?.timeFormat || 'hh:mm A'}`
                    )}
                  </TextStyle>
                </div>
              </div>

              {children}

              <ItemHolders className="mt-10">
                {conversation?.attachments?.map((item) => {
                  const module = ALL_MODULES.clientConversationAttachment;
                  const details = {
                    fileName: item?.fileName,
                    conversationId: conversation?.conversationId,
                    clientId: clientId
                  };
                  const url = getDownloadURL({ module, details });
                  return (
                    <AttachmentsContainer key={item.fileName}>
                      <Link url={url} removeUnderline>
                        {item?.originalFileName}
                      </Link>
                    </AttachmentsContainer>
                  );
                })}
              </ItemHolders>
              {isConversationViewed && (
                <div className="relative flex jend">
                  <Tooltip
                    html={
                      <div>
                        {getTranslatedText(
                          intl,
                          'invoice.conversation.mail.opened'
                        )}
                      </div>
                    }
                    delay={300}
                    duration={250}
                    position="bottom-start"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                  >
                    <div className="position-absolute br0">
                      <SVGIcon name="EyeVisible" />
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </Card>
      </Tag>
    </div>
  );
}

// Shared Reply Widget Component
export function ReplyWidget({ toggleThreadReply }) {
  return (
    <div onClick={toggleThreadReply} className="flex center p20 replyWidget">
      <div className="fw text-align-centr">
        <Card padding="sizeExtraSmall" rounded className="flex col">
          <Button
            plain
            icon={<SVGIcon color={COLOR_MAP.blue} name="Reply" />}
            onClick={toggleThreadReply}
          >
            <FormattedMessage id="app.sideConversation.reply.to" />
          </Button>
        </Card>
      </div>
    </div>
  );
}

// Shared Compose Section Component
export function ComposeSection({
  isMSP,
  allToUsers,
  updateCCState,
  showCC,
  showBCC,
  ccUsers,
  setCCUsers,
  bccUsers,
  setBCCUsers,
  clientId,
  intl,
  state,
  updateTemplate,
  handleFileAttachments,
  handleAttachmentsDeletion,
  toggleThreadReply,
  loading,
  isCreateAuthorised,
  composeMail
}) {
  return (
    <div className="white-to-grey-bg flex col rounded-2xl pb10 position-relative m20 bshadow">
      <div>
        {isMSP && (
          <div className="flex center mt-10 ml-10">
            <T1 className="mr-10">
              <TextStyle weight="SemiBold">
                <FormattedMessage id="ticket.details.recepient.to" />
              </TextStyle>
            </T1>
            <MultipleNamesHandler
              data={allToUsers.map((el) => {
                return {
                  name: el.email
                };
              })}
            />
            <div className="flex mlauto mt-10">
              <div className="mlauto">
                <HiddenActions updateState={updateCCState} />
              </div>
            </div>
          </div>
        )}
        <div>
          {showCC && (
            <CopyMailUsers className="flex mt-10 ml-10 fg1 center">
              <div className="title">
                <FormattedMessage id="client.chat.recepient.cc" />
              </div>
              <SelectUsers
                updateField={(val) => {
                  setCCUsers(val);
                }}
                property="userId"
                query={GET_CLIENT_USERS}
                params={{
                  client: AccountClient,
                  variables: { input: { clientId: clientId } }
                }}
                methodName="getClientUserAssociationsList"
                field="cc"
                value={ccUsers}
                isMulti
                creatable
                skip={!clientId}
                placeholder={getTranslatedText(
                  intl,
                  'client.email.chat.pl.choose'
                )}
              />
            </CopyMailUsers>
          )}
          {showBCC && (
            <CopyMailUsers className="flex ml-10 mt-10 fg1 center">
              <div className="title">
                <FormattedMessage id="client.chat.recepient.bcc" />
              </div>
              <SelectUsers
                updateField={(val) => {
                  setBCCUsers(val);
                }}
                property="userId"
                methodName="getClientUserAssociationsList"
                query={GET_CLIENT_USERS}
                params={{
                  client: AccountClient,
                  variables: { input: { clientId: clientId } }
                }}
                field="bcc"
                value={bccUsers}
                isMulti
                creatable
                skip={!clientId}
                placeholder={getTranslatedText(
                  intl,
                  'client.email.chat.pl.choose'
                )}
              />
            </CopyMailUsers>
          )}
        </div>
      </div>
      <EditorHolder>
        <EmailEditor
          template={state.template}
          updateTemplate={updateTemplate}
          handleFileAttachments={handleFileAttachments}
          handleAttachmentsDeletion={handleAttachmentsDeletion}
          placeholder={getTranslatedText(intl, 'client.details.mail.desc')}
          module="CLIENT_CONVERSATION"
          details={{
            operation: 'CREATE',
            clientId: clientId
          }}
        />
      </EditorHolder>
      <div className="flex center action-buttons">
        <ButtonGroup spacing="loose">
          <Button plainSecondary onClick={toggleThreadReply}>
            <FormattedMessage id="common.cancel" />
          </Button>

          <Button
            loading={loading}
            primary
            disabled={!isCreateAuthorised}
            onClick={composeMail}
          >
            <FormattedMessage id="common.send" />
          </Button>
        </ButtonGroup>
      </div>
    </div>
  );
}

// Shared HiddenActions Component
export function HiddenActions(props) {
  const [ccVisible, setCcVisible] = useState(false);
  const [bccVisible, setBccVisible] = useState(false);

  const handleCc = () => {
    setCcVisible(true);
    props.updateState('cc', true);
  };

  const handleBcc = () => {
    setBccVisible(true);
    props.updateState('bcc', true);
  };

  return (
    <div className="flex cennter mlauto">
      {!ccVisible && (
        <div className="text-medium mr-12 cursor-pointer" onClick={handleCc}>
          <FormattedMessage id="client.chat.recepient.cc" />
        </div>
      )}
      {!bccVisible && (
        <div className="text-medium mr-12 cursor-pointer" onClick={handleBcc}>
          <FormattedMessage id="client.chat.recepient.bcc" />
        </div>
      )}
    </div>
  );
}

function CommonConversations(props) {
  return null;
}

export default injectIntl(CommonConversations);
